import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, CheckCircle2, XCircle, Download, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const MANAGERS = ["Abhishek Singh", "Om Prakash Sahu", "Fatima Shabnam"];

export default function TasksPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const isManager = Boolean(user?.manager_name);
  const today = new Date().toISOString().slice(0, 10);

  const [tasks, setTasks] = useState([]);
  const [managerFilter, setManagerFilter] = useState(isManager ? user.manager_name : "all");
  const [kindFilter, setKindFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ manager: isManager ? user.manager_name : "Abhishek Singh", text: "", kind: "permanent", hour: "", date: today });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { on_date: selectedDate };
      if (managerFilter !== "all") params.manager = managerFilter;
      if (kindFilter !== "all") params.kind = kindFilter;
      const { data } = await api.get("/tasks", { params });
      setTasks(data);
    } finally { setLoading(false); }
  }, [selectedDate, managerFilter, kindFilter]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tasks", {
        manager: form.manager,
        text: form.text,
        kind: form.kind,
        hour: form.hour || null,
        date: form.kind === "daily" ? form.date : null,
      });
      toast.success("Task added");
      setOpen(false);
      setForm({ ...form, text: "", hour: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const setStatus = async (task, status) => {
    try {
      await api.post(`/tasks/${task.id}/status`, { date: selectedDate, status });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
    }
  };

  const del = async (task) => {
    if (!window.confirm(`Delete task "${task.text}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const downloadMonthly = async () => {
    setDownloading(true);
    try {
      const [year, month] = selectedDate.split("-").map(Number);
      const params = { year, month };
      if (managerFilter !== "all") params.manager = managerFilter;
      const res = await api.get("/tasks/monthly-pdf", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `royal-wow-tasks-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Download failed");
    } finally { setDownloading(false); }
  };

  const statusFor = (t) => (t.status_by_date || {})[selectedDate] || "pending";

  return (
    <div data-testid="tasks-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Responsibilities</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2 flex items-center gap-3">
            Tasks <ClipboardList className="w-8 h-8 text-primary" />
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Permanent tasks recur every day. Daily tasks are one-offs. Managers mark them completed before the day ends.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={downloadMonthly} disabled={downloading} variant="outline" className="rounded-full" data-testid="tasks-monthly-pdf">
            <Download className="w-4 h-4 mr-2" />{downloading ? "…" : "Monthly PDF"}
          </Button>
          <Button onClick={() => setOpen(true)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-task-btn">
            <Plus className="w-4 h-4 mr-2" /> New task
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Date</Label>
          <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} data-testid="task-date-filter" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Manager</Label>
          <Select value={managerFilter} onValueChange={setManagerFilter} disabled={isManager}>
            <SelectTrigger data-testid="task-manager-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All managers</SelectItem>
              {MANAGERS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Kind</Label>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger data-testid="task-kind-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="permanent">Permanent</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 bg-white border border-border">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No tasks in this view.</p>
            <p className="text-muted-foreground mt-2">Tap "New task" to create one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]" data-testid="tasks-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3 font-normal">Kind</th>
                  <th className="px-4 py-3 font-normal">Hour</th>
                  <th className="px-4 py-3 font-normal">Manager</th>
                  <th className="px-4 py-3 font-normal">Task</th>
                  <th className="px-4 py-3 font-normal text-center">Status ({selectedDate})</th>
                  <th className="px-4 py-3 font-normal text-right"></th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => {
                  const st = statusFor(t);
                  const canMark = isOwner || (isManager && t.manager === user.manager_name);
                  return (
                    <tr key={t.id} className="border-t border-border" data-testid={`task-row-${t.id}`}>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-full ${t.kind === "permanent" ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-800"}`}>
                          {t.kind}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-sm">{t.hour || "—"}</td>
                      <td className="px-4 py-3 font-medium">{t.manager}</td>
                      <td className="px-4 py-3">{t.text}</td>
                      <td className="px-4 py-3 text-center">
                        {canMark ? (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant={st === "completed" ? "default" : "outline"} onClick={() => setStatus(t, "completed")} data-testid={`task-complete-${t.id}`} className="h-8">
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant={st === "not_completed" ? "destructive" : "outline"} onClick={() => setStatus(t, "not_completed")} data-testid={`task-nc-${t.id}`} className="h-8">
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className={`text-xs ${st === "completed" ? "text-emerald-700" : st === "not_completed" ? "text-red-600" : "text-muted-foreground"}`}>
                            {st === "completed" ? "✓ Done" : st === "not_completed" ? "✗ Missed" : "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canMark && (
                          <Button size="sm" variant="ghost" onClick={() => del(t)} data-testid={`task-delete-${t.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="task-dialog" className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New task</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Manager</Label>
              {isManager ? (
                <Input value={user.manager_name} disabled className="bg-secondary" />
              ) : (
                <Select value={form.manager} onValueChange={(v) => setForm({ ...form, manager: v })}>
                  <SelectTrigger data-testid="task-form-manager"><SelectValue /></SelectTrigger>
                  <SelectContent>{MANAGERS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="task-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="permanent">Permanent (every day this month)</SelectItem>
                  <SelectItem value="daily">Daily (one specific date)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hour (optional)</Label>
                <Input type="time" value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} data-testid="task-form-hour" />
              </div>
              {form.kind === "daily" && (
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="task-form-date" required />
                </div>
              )}
            </div>
            <div>
              <Label>Task description</Label>
              <Textarea rows={2} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="task-form-text" required maxLength={500} placeholder="e.g. Check cold storage temperature" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving || !form.text.trim()} data-testid="task-form-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {saving ? "Saving…" : "Add task"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
